import { useCallback, useMemo } from "react";
import { useBooleanFlagValue, useObjectFlagValue } from "@openfeature/react-sdk";
import flags from "../features/featureFlags/flags.js";

/**
 * Maps each whitelabel slug to its Flagsmith feature flag.
 * The slug keys match the identifiers used throughout the app
 * (LoginPage, ProtectedRoute, etc.).
 */
const WHITELABEL_FLAG_MAP = {
  aidh: flags.AIDH_ENABLED,
  crush: flags.DRTC_ENABLED,
  obesity: flags.OBESITY_ENABLED,
};

const isFlagEnabledByDate = (flagValue, flagNameForLog) => {
  if (!flagValue) return false;
  if (typeof flagValue !== "object") return Boolean(flagValue);
  if (!flagValue.availableFrom) return true;

  try {
    return new Date() >= new Date(flagValue.availableFrom);
  } catch (error) {
    console.warn(`Invalid ${flagNameForLog} availableFrom date`, error);
    return false;
  }
};

const formatAvailableFromDate = (flagValue, flagNameForLog) => {
  if (!flagValue || typeof flagValue !== "object" || !flagValue.availableFrom) {
    return null;
  }

  try {
    return new Intl.DateTimeFormat("en-AU", {
      day: "numeric",
      month: "long",
    }).format(new Date(flagValue.availableFrom));
  } catch (error) {
    console.warn(`Invalid ${flagNameForLog} availableFrom date for formatting`, error);
    return null;
  }
};

/**
 * Reads the whitelabel availability feature flags and returns helpers
 * that the rest of the app can use to show/hide each whitelabel product.
 *
 * @returns {{
 *   isAidhEnabled: boolean,
 *   isDrtcEnabled: boolean,
 *   isObesityEnabled: boolean,
 *   drtcAvailableFrom: string|null,
 *   isWhitelabelEnabled: (slug: string) => boolean,
 *   filterByAvailability: (slugs: string[]) => string[],
 * }}
 */
export const useWhitelabelAvailability = () => {
  const drtcFlag = useObjectFlagValue(flags.DRTC_ENABLED, null);
  const aidhFlag = useObjectFlagValue(flags.AIDH_ENABLED, null);
  const isDrtcEnabled = useMemo(() => isFlagEnabledByDate(drtcFlag, "drtc_enabled"), [drtcFlag]);
  const isAidhEnabled = useMemo(() => isFlagEnabledByDate(aidhFlag, "aidh_enabled"), [aidhFlag]);
  const drtcAvailableFrom = useMemo(
    () => formatAvailableFromDate(drtcFlag, "drtc_enabled"),
    [drtcFlag],
  );
  const aidhAvailableFrom = useMemo(
    () => formatAvailableFromDate(aidhFlag, "aidh_enabled"),
    [aidhFlag],
  );
  const isObesityEnabled = useBooleanFlagValue(flags.OBESITY_ENABLED, false);

  const enabledMap = useMemo(
    () => ({
      aidh: isAidhEnabled,
      crush: isDrtcEnabled,
      drtc: isDrtcEnabled, // alias used in some flows
      obesity: isObesityEnabled,
      obesid: isObesityEnabled, // alias used in subscription flow
      tg: true, // TG is always available
    }),
    [isAidhEnabled, isDrtcEnabled, isObesityEnabled],
  );

  /** Check whether a single whitelabel slug is enabled */
  const isWhitelabelEnabled = useCallback((slug) => enabledMap[slug] ?? true, [enabledMap]);

  /** Filter an array of slugs, keeping only the ones whose flag is on */
  const filterByAvailability = useCallback(
    (slugs) => slugs.filter((s) => isWhitelabelEnabled(s)),
    [isWhitelabelEnabled],
  );

  return {
    isAidhEnabled,
    isDrtcEnabled,
    drtcAvailableFrom,
    aidhAvailableFrom,
    isObesityEnabled,
    isWhitelabelEnabled,
    filterByAvailability,
  };
};

export { WHITELABEL_FLAG_MAP };
export default useWhitelabelAvailability;
